import { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "@/core/api/client";
import { Button } from "@/core/ui";
import type { Session } from "./types";
import { sessionKey, useSession } from "./useSession";

/** Only a path inside the SPA: a token pasted into a crafted link must not send anyone away. */
export function safeNext(raw: string | null): string {
  if (!raw || !raw.startsWith("/") || raw.startsWith("//") || raw.startsWith("/\\")) return "/";
  return raw;
}

/**
 * Landing route of the sign-in link carried by notification e-mails (ADR 0022).
 *
 * The token is redeemed with a POST, never by the GET that opened the page: link scanners
 * in mail clients follow GETs, and a link they had burnt would sign nobody in. A visitor
 * who is already signed in is sent on without spending the token, which is what makes one
 * token per e-mail enough for a digest with several links.
 */
export function LoginLinkPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const session = useSession();
  const token = params.get("token") ?? "";
  const next = safeNext(params.get("next"));
  const attempted = useRef(false);

  const redeem = useMutation({
    mutationFn: () => api<Session>("/api/auth/link/", { method: "POST", json: { token } }),
    onSuccess: (data) => {
      qc.setQueryData(sessionKey, {
        ...data,
        sso_provider_id: qc.getQueryData<Session>(sessionKey)?.sso_provider_id ?? "",
      });
      navigate(next, { replace: true });
    },
  });

  useEffect(() => {
    if (session.isPending || attempted.current) return;
    attempted.current = true;
    if (session.data?.authenticated) {
      navigate(next, { replace: true });
      return;
    }
    if (token) redeem.mutate();
  }, [session.isPending, session.data, token, next, navigate, redeem]);

  if (redeem.isError || !token) {
    return (
      <div className="bt-login-link">
        <p className="bt-page-message bt-page-message--error">{t("auth.link_invalid")}</p>
        <Button
          className="bt-login-link__sign-in"
          onClick={() => navigate(`/auth/login?next=${encodeURIComponent(next)}`)}
        >
          {t("auth.link_sign_in_instead")}
        </Button>
      </div>
    );
  }
  return <p className="bt-page-message">{t("auth.link_signing_in")}</p>;
}
